import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { onValue, ref } from 'firebase/database';
import { auth, db, CENTER_ID } from '@/shared/firebase/config';
import {
  loadStoredAccessCode,
  clearStoredAccessCode,
  storeAccessCode,
  type AccessCode,
} from '@/shared/firebase/codes';

type Ctx = {
  user: User | null;
  loading: boolean;
  /** Centro de esta instancia (fijo, viene de VITE_CENTER_ID). */
  centerId: string;
  accessCode: AccessCode | null;
  setAccessCode: (c: AccessCode | null) => void;
  isAuthenticated: boolean;
  /** El último intento de validar el código devolvió revocado/inexistente. */
  codeInvalid: boolean;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessCode, setAccessCodeState] = useState<AccessCode | null>(() => loadStoredAccessCode());
  const [codeInvalid, setCodeInvalid] = useState(false);
  const seenInitialValue = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  // Validación en tiempo real del código: si se borra o revoca, expulsamos.
  // Si cambian residentUid / residentUids / label, mantenemos sincronizado.
  useEffect(() => {
    if (!accessCode) {
      seenInitialValue.current = false;
      setCodeInvalid(false);
      return;
    }
    const codeKey = accessCode.code;
    seenInitialValue.current = false;
    return onValue(
      ref(db, `access_codes/${codeKey}`),
      (snap) => {
        const fresh = snap.val() as AccessCode | null;
        if (!fresh || fresh.revoked) {
          // Solo expulsamos cuando ya habíamos confirmado al menos una vez
          // (evita falsos positivos durante el render inicial offline).
          if (seenInitialValue.current) {
            setCodeInvalid(true);
            setAccessCodeState(null);
            clearStoredAccessCode();
          }
          return;
        }
        seenInitialValue.current = true;
        // Sincronizar si cambió algún campo relevante
        const changed =
          fresh.role !== accessCode.role ||
          fresh.residentUid !== accessCode.residentUid ||
          fresh.label !== accessCode.label;
        if (changed) {
          setAccessCodeState(fresh);
          storeAccessCode(fresh);
        }
      },
      // Error: no se pudo leer (regla denegada). No expulsamos —
      // el usuario podría no tener cobertura. Quedará expulsado en próxima validación.
      () => undefined,
    );
  }, [accessCode?.code]); // eslint-disable-line react-hooks/exhaustive-deps

  function setAccessCode(c: AccessCode | null) {
    setCodeInvalid(false);
    setAccessCodeState(c);
    if (c) storeAccessCode(c);
    else clearStoredAccessCode();
  }

  async function logout() {
    try { if (user) await signOut(auth); } catch { /* ignore */ }
    setAccessCode(null);
  }

  const isAuthenticated = !!user || !!accessCode;

  return (
    <AuthCtx.Provider
      value={{
        user, loading,
        centerId: CENTER_ID,
        accessCode, setAccessCode,
        isAuthenticated, codeInvalid, logout,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
